// 완성된 ReportEvent 를 Sentry 문법으로 옮겨 적는다. Sentry SDK 를 import 하지 않고 필요한
// 메서드만 가진 SentryLike 를 받는다 — shared 가 Sentry 에 묶이지 않고 가짜 객체로 테스트할 수 있다.
// @sentry/react 의 Sentry 네임스페이스가 이 인터페이스를 그대로 만족한다.

import type { ErrorMechanism } from '../AppError'
import type { Breadcrumb, FwTag, ReportAdapter, ReportUser } from '../reporter'
import type { ReportEvent, ReportLevel } from '../toReportEvent'

export type SentryScopeLike = {
  setLevel(level: ReportLevel): void
  setTag(key: string, value: string): void
  setContext(name: string, context: Record<string, unknown>): void
  setFingerprint(fingerprint: string[]): void
}

// Sentry 의 EventHint.mechanism 과 같은 모양. handled=false 여야 Sentry 가 "unhandled" 로 집계한다(D-12 지표 4).
export type SentryCaptureHint = { mechanism: { type: string; handled: boolean } }

export type SentryLike = {
  captureException(exception: unknown, hint?: SentryCaptureHint): unknown
  withScope(callback: (scope: SentryScopeLike) => void): void
  setUser(user: ReportUser): void
  setTag(key: string, value: string): void
  addBreadcrumb(crumb: {
    category?: string
    message?: string
    data?: Record<string, unknown>
  }): void
}

// 코드가 받지 못한 경로(전역 리스너·렌더 크래시)만 unhandled. 부팅 catch·직접 호출·인터셉터는 코드가 받은 것이다.
const UNHANDLED_MECHANISMS: readonly ErrorMechanism[] = [
  'onerror',
  'unhandledrejection',
  'boundary',
]

const captureHint = (mechanism: ErrorMechanism | undefined): SentryCaptureHint | undefined =>
  mechanism
    ? { mechanism: { type: mechanism, handled: !UNHANDLED_MECHANISMS.includes(mechanism) } }
    : undefined

export const createSentryAdapter = (sentry: SentryLike): ReportAdapter => ({
  send(event: ReportEvent) {
    sentry.withScope((scope) => {
      scope.setLevel(event.level)
      for (const [key, value] of Object.entries(event.tags)) {
        if (value !== undefined) scope.setTag(key, value)
      }
      if (event.fingerprint) scope.setFingerprint(event.fingerprint)
      if (Object.keys(event.contexts).length > 0) scope.setContext('fw', event.contexts)
      const hint = captureHint(event.error.mechanism)
      if (hint) sentry.captureException(event.original, hint)
      else sentry.captureException(event.original)
    })
  },
  setUser(user) {
    sentry.setUser(user)
  },
  setTags(tags: Partial<Record<FwTag, string>>) {
    for (const [key, value] of Object.entries(tags)) {
      if (value !== undefined) sentry.setTag(key, value)
    }
  },
  addBreadcrumb(crumb: Breadcrumb) {
    sentry.addBreadcrumb(crumb)
  },
})
